import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bot, CalendarOff, Crown, FolderOpen, Globe, Handshake, Inbox, MessageSquare, ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectIsAuthenticated,
  selectIsAdmin,
  selectIsOrgOwnerOrAdmin,
  selectUserProfile,
  logoutAsync,
} from "../../features/authentication/authenticationSlice";
import { ROUTES } from "../../routes";
import { useTheme } from "../../features/theme/useTheme";
import { selectInboxCount } from "../../features/trial-requests/trial-requests-slice";
import { fetchInboxCount } from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { useUnreadCount } from "../../features/messages/hooks/useUnreadCount";
import { STAFF_CHANNEL } from "../../features/messages/models/MessagesChannel";
import CreateSessionButton from "../button/CreateSessionButton";
import ThemeToggle from "../ToggleSwitch";
import GetTheAppDialog from "../app-stores/GetTheAppDialog";
import {
  AttendanceNavIcon,
  BillingNavIcon,
  CalendarNavIcon,
  HomeNavIcon,
  LogoutNavIcon,
  MembersNavIcon,
  NAV_STROKE_WIDTH,
  SessionsNavIcon,
  SettingsNavIcon,
} from "../navigation/NavIcons";

const SectionLabel = ({ label }: { label: string }) => (
  <p className="mt-4 mb-1 px-4 text-xs font-semibold uppercase tracking-wider text-grey-50 dark:text-grey-60">
    {label}
  </p>
);

interface DrawerItemProps {
  to: string;
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onNavigate: () => void;
  badge?: number;
}

/** One drawer entry — a real link (middle-click friendly) that closes the drawer. */
/** A compact icon-over-label tile for the rarely used tools row. */
const ToolTile = ({ to, active, icon, label, onNavigate }: Omit<DrawerItemProps, "badge">) => (
  <Link
    to={to}
    onClick={onNavigate}
    className={`mobile-menu-link flex-col gap-1 px-1 py-2 text-center text-[11px] ${active ? "active" : ""}`}
  >
    {icon}
    <span className="w-full truncate">{label}</span>
  </Link>
);

const DrawerItem = ({ to, active, icon, label, onNavigate, badge }: DrawerItemProps) => (
  <li>
    <Link
      to={to}
      className={`mobile-menu-link ${active ? "active" : ""}`}
      onClick={onNavigate}
    >
      {icon}
      {label}
      {badge != null && badge > 0 && (
        <span className="ml-auto rounded-full bg-primary-40 px-2 py-0.5 text-xs font-semibold text-white dark:bg-primary-90 dark:text-primary-10">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </Link>
  </li>
);

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The slide-in mobile drawer plus its backdrop overlay. Fully state-driven:
 * Header owns the open flag (burger toggles it); Escape, the backdrop, a
 * viewport resize to desktop, and every navigation close it via onClose.
 */
const MobileMenu = ({ open, onClose }: MobileMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const inboxCount = useAppSelector(selectInboxCount);
  // LeftMenu keeps the count fresh on every page; the open drawer refreshes it too.
  const unreadMessages = useUnreadCount(STAFF_CHANNEL, open && isAuthenticated);

  // Refresh the Inbox badge whenever the drawer opens.
  useEffect(() => {
    if (open && isAuthenticated) {
      dispatch(fetchInboxCount());
    }
  }, [open, isAuthenticated, dispatch]);
  const isAdmin = useAppSelector(selectIsAdmin);
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const userProfile = useAppSelector(selectUserProfile);
  const { themeMode, changeTheme } = useTheme();
  const [getAppOpen, setGetAppOpen] = useState(false);

  const firstName = userProfile?.name?.split(" ")[0] || "";
  const initials = userProfile?.name
    ? userProfile.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "";

  const isActiveRoute = (route: string) => {
    if (route === ROUTES.HOME_PAGE) {
      return (
        location.pathname === ROUTES.HOME_PAGE ||
        location.pathname === ROUTES.HOME
      );
    }
    if (route === ROUTES.CALENDAR) return location.pathname.startsWith("/calendar");
    if (route === ROUTES.ATTENDANCE) return location.pathname.startsWith("/attendance");
    if (route === ROUTES.SESSIONS) return location.pathname.startsWith("/sessions");
    if (route === ROUTES.MEMBERS) return location.pathname.startsWith("/members");
    if (route === ROUTES.MESSAGES) return location.pathname.startsWith("/messages");
    if (route === ROUTES.BILLING) return location.pathname.startsWith("/billing");
    if (route === ROUTES.ADMIN_USERS) return location.pathname.startsWith("/admin");
    return location.pathname === route;
  };

  const handleLogout = async () => {
    onClose();
    await dispatch(logoutAsync());
    navigate(ROUTES.LOGIN);
  };

  // While open: Escape closes, the body doesn't scroll, and growing the
  // viewport past the mobile breakpoint dismisses the drawer.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const handleResize = () => {
      if (window.innerWidth >= 1024) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    document.body.classList.add("overflow-hidden");
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open, onClose]);

  return (
    <div className="lg:hidden">
      {/* Mobile Menu container */}
      <div
        id="mobile-menu"
        aria-hidden={!open}
        inert={!open}
        className={`dark:bg-primary-20 fixed top-0 left-0 z-10 h-full w-[300px] transform bg-white shadow-xl transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {isAuthenticated ? (
          <div className="flex h-full w-full flex-col">
            {/* Profile */}
            <button className="bg-grey-95 dark:bg-primary-10 dark:border-primary-10 border-grey-95 flex items-center justify-between border p-4 pl-6">
              <div className="flex items-center gap-2">
                <div className="relative flex size-12 items-center justify-center rounded-full border border-[#367D7D] bg-[#367D7D] dark:border-white">
                  <span className="font-bold text-white">{initials || "TN"}</span>
                </div>
                <div className="flex flex-col items-start">
                  <h4 className="">{t("header.hello", { firstName })}</h4>
                  <p className="text-sm">{t("header.instructorAccount")}</p>
                </div>
              </div>
              <svg
                width={10}
                height={20}
                viewBox="0 0 10 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="dark:fill-white"
                  d="M9.74867 10.66L1.74867 19.66C1.38391 20.0737 0.753068 20.114 0.338672 19.75C-0.0750326 19.3852 -0.115299 18.7544 0.248672 18.34L7.65867 10L0.248672 1.66C-0.0871302 1.24308 -0.0350415 0.635662 0.36684 0.282006C0.768721 -0.0716495 1.37783 -0.0460871 1.74867 0.339997L9.74867 9.34C10.0803 9.7175 10.0803 10.2825 9.74867 10.66Z"
                  fill="#4D4D4D"
                />
              </svg>
            </button>
            {/* Menu — scrolls independently so short screens reach every item. */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-2 pb-8">
              <nav className="w-full flex-1" role="menu">
                <ul className="space-y-1">
                  <DrawerItem to={ROUTES.HOME_PAGE} active={isActiveRoute(ROUTES.HOME_PAGE)} icon={<HomeNavIcon />} label={t("navigation.home")} onNavigate={onClose} />
                </ul>
                <SectionLabel label={t("navigation.groupSchedule", "Schedule")} />
                <ul className="space-y-1">
                  <DrawerItem to={ROUTES.CALENDAR} active={isActiveRoute(ROUTES.CALENDAR)} icon={<CalendarNavIcon />} label={t("navigation.calendar")} onNavigate={onClose} />
                  <DrawerItem to={ROUTES.ATTENDANCE} active={isActiveRoute(ROUTES.ATTENDANCE)} icon={<AttendanceNavIcon />} label={t("navigation.attendance")} onNavigate={onClose} />
                  <DrawerItem to={ROUTES.SESSIONS} active={isActiveRoute(ROUTES.SESSIONS)} icon={<SessionsNavIcon />} label={t("navigation.sessions")} onNavigate={onClose} />
                  <DrawerItem to={ROUTES.ABSENCES} active={isActiveRoute(ROUTES.ABSENCES)} icon={<CalendarOff size={24} strokeWidth={NAV_STROKE_WIDTH} />} label={t("navigation.absences", "Absences")} onNavigate={onClose} />
                </ul>
                <SectionLabel label={t("navigation.groupPeople", "People")} />
                <ul className="space-y-1">
                  <DrawerItem to={ROUTES.MEMBERS} active={isActiveRoute(ROUTES.MEMBERS)} icon={<MembersNavIcon />} label={t("navigation.members")} onNavigate={onClose} />
                  <DrawerItem to={ROUTES.MESSAGES} active={isActiveRoute(ROUTES.MESSAGES)} icon={<MessageSquare size={24} strokeWidth={NAV_STROKE_WIDTH} />} label={t("messages.nav.messages")} onNavigate={onClose} badge={unreadMessages} />
                  <DrawerItem to={ROUTES.TRIAL_REQUESTS} active={isActiveRoute(ROUTES.TRIAL_REQUESTS)} icon={<Inbox size={24} strokeWidth={NAV_STROKE_WIDTH} />} label={t("navigation.trialRequests")} onNavigate={onClose} badge={inboxCount} />
                  <DrawerItem to={ROUTES.MATERIALS} active={isActiveRoute(ROUTES.MATERIALS)} icon={<FolderOpen size={24} strokeWidth={NAV_STROKE_WIDTH} />} label={t("navigation.materials", "Materials")} onNavigate={onClose} />
                </ul>
                <SectionLabel label={t("navigation.groupBusiness", "Business")} />
                <ul className="space-y-1">
                  <DrawerItem to={ROUTES.WEBSITE} active={isActiveRoute(ROUTES.WEBSITE)} icon={<Globe size={24} strokeWidth={NAV_STROKE_WIDTH} />} label={t("navigation.website")} onNavigate={onClose} />
                  {isOrgOwnerOrAdmin && (
                    <DrawerItem to={ROUTES.BILLING} active={isActiveRoute(ROUTES.BILLING)} icon={<BillingNavIcon />} label={t("navigation.billing")} onNavigate={onClose} />
                  )}
                  <DrawerItem to={ROUTES.REFERRAL_PROGRAM} active={isActiveRoute(ROUTES.REFERRAL_PROGRAM)} icon={<Handshake size={24} strokeWidth={NAV_STROKE_WIDTH} />} label={t("navigation.referrals")} onNavigate={onClose} />
                  <DrawerItem to={ROUTES.SUBSCRIPTION} active={isActiveRoute(ROUTES.SUBSCRIPTION)} icon={<Crown size={24} className="text-grey-50" />} label={t("header.subscription")} onNavigate={onClose} />
                </ul>
                <hr className="border-grey-70 my-3" />
                {/* Rarely used tools: a compact tile row instead of four full rows. */}
                <div className="grid grid-cols-4 gap-1">
                  <ToolTile to={ROUTES.ASSISTANT} active={isActiveRoute(ROUTES.ASSISTANT)} icon={<Bot size={20} strokeWidth={NAV_STROKE_WIDTH} />} label={t("navigation.assistant")} onNavigate={onClose} />
                  <ToolTile to={ROUTES.ASSISTANT_IMPORT} active={isActiveRoute(ROUTES.ASSISTANT_IMPORT)} icon={<Sparkles size={20} strokeWidth={NAV_STROKE_WIDTH} />} label={t("navigation.import")} onNavigate={onClose} />
                  {isAdmin && (
                    <ToolTile to={ROUTES.ADMIN_USERS} active={isActiveRoute(ROUTES.ADMIN_USERS)} icon={<ShieldCheck width={20} height={20} className="text-grey-50 dark:text-grey-60" />} label={t("navigation.admin")} onNavigate={onClose} />
                  )}
                  <ToolTile to={ROUTES.SETTINGS} active={isActiveRoute(ROUTES.SETTINGS)} icon={<SettingsNavIcon />} label={t("header.settings")} onNavigate={onClose} />
                  <button type="button" onClick={() => { onClose(); setGetAppOpen(true); }} className="mobile-menu-link flex-col gap-1 px-1 py-2 text-center text-[11px]">
                    <Smartphone size={20} strokeWidth={NAV_STROKE_WIDTH} />
                    <span className="w-full truncate">{t("getTheApp.title")}</span>
                  </button>
                </div>
              </nav>
              <hr className="border-grey-70 my-2" />
              {/* Theme Toggle Section */}
              <div className="px-4 py-3">
                <ThemeToggle mode={themeMode} onChange={changeTheme} />
              </div>
              <div className="mt-6 flex shrink-0 justify-center">
                <CreateSessionButton text={t("navigation.newSession")} />
              </div>
              <div className="mt-4 px-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-md transition-colors duration-200 border border-red-200 dark:border-red-800"
                >
                  <LogoutNavIcon />
                  {t("header.logout")}
                </button>
              </div>
            </div>
          </div>
        ) : location.pathname !== ROUTES.LOGIN ? (
          <div className="flex h-full w-full flex-col">
            <div className="px-6 py-4">
              <Link
                to={ROUTES.LOGIN}
                onClick={onClose}
                className="btn btn-primary w-full justify-center"
              >
                {t("header.login", "Login")}
              </Link>
            </div>
          </div>
        ) : null}
      </div>
      {getAppOpen && <GetTheAppDialog onClose={() => setGetAppOpen(false)} />}
      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu-overlay"
        onClick={onClose}
        aria-hidden="true"
        className={`fixed inset-0 z-0 bg-black/80 transition-opacity duration-300 ease-in-out ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
    </div>
  );
};

export default MobileMenu;
